import type {
  BroadRequest,
  BroadResponse,
  ClashConfig,
  ClashElementInput,
} from './clashTypes'
import { normalizeClashConfig } from './clashTypes'

// ---------------------------------------------------------------------------
// Broad-phase: de N elementos para os pares que VALE A PENA testar.
//
// Porque nao O(n^2): 50 mil elementos dao 1,25 mil milhoes de pares. Ao
// ritmo optimista de 100 milhoes de testes de caixa por segundo isso sao
// 12 segundos so para dizer "nao". Inaceitavel para um botao de UI.
//
// Algoritmo: HASH ESPACIAL uniforme (Teschner et al. 2003). Cada
// elemento e inscrito nas celulas que a sua caixa toca; so se testam
// pares que partilham celula. A tabela e um `Map` indexado por um hash
// das tres coordenadas inteiras da celula, por isso a memoria depende da
// OCUPACAO e nao da extensao do modelo (um modelo georreferenciado com
// coordenadas na casa do milhao nao cria uma grelha densa gigante).
// Colisoes de hash so criam candidatos a mais, que o teste de caixa
// seguinte deita fora: nunca falsos negativos.
//
// TAMANHO DA CELULA: mediana da maior aresta das caixas. A mediana nao
// se move por causa de meia duzia de elementos enormes (a laje do piso,
// a fachada inteira) nem por causa de milhares de parafusos. Depois ha
// uma malha de seguranca: se o total de inscricoes passar de
// INSERT_BUDGET x N, a celula duplica e recomeca (no maximo
// MAX_REGROW vezes). Isso trava o caso patologico "mediana minuscula
// mais um modelo com quilometros de extensao".
//
// ELEMENTOS ENORMES: uma caixa que toque mais do que CELL_SPAN_CAP
// celulas nao e inscrita (encheria a tabela); vai para uma lista a
// parte, testada contra todos os outros. Sao poucos por definicao (a
// mediana garante-o), por isso o custo O(k x n) e residual.
//
// FOLGA: quando ha `clearance`, cada caixa e inflada por essa folga
// antes de entrar na grelha e o criterio de aceitacao passa a ser
// "afastamento entre caixas <= clearance". Assim a broad-phase nunca
// perde um par de folga (inflar as duas caixas apanharia ate 2x a
// folga; o teste de afastamento aperta de volta).
//
// SAIDA DETERMINISTA: os pares saem ordenados por (a, b) com a < b.
// Mesma entrada, mesma saida, sempre.
// ---------------------------------------------------------------------------

/** Inscricoes por elemento acima das quais a celula duplica. */
const INSERT_BUDGET = 8
const MAX_REGROW = 8
/** Celulas que uma caixa pode tocar antes de ser tratada como enorme. */
const CELL_SPAN_CAP = 4096
/** Celula minima, em metros. Evita divisoes por zero em modelos
 *  degenerados (tudo no mesmo ponto). */
const MIN_CELL = 0.01

export interface BroadPhaseOutcome extends BroadResponse {
  /** Elementos que ficaram de fora por filtro de categoria. */
  filtered: number
}

/** Empacota elementos "amigaveis" no formato plano do pedido. Usado
 *  pelo orquestrador e pelos testes. As categorias sao normalizadas
 *  para maiusculas. */
export function packBroadRequest(
  elements: readonly ClashElementInput[],
  config?: Partial<ClashConfig> | null,
): BroadRequest {
  const n = elements.length
  const boxes = new Float64Array(n * 6)
  const modelIndex = new Uint32Array(n)
  const categoryIndex = new Int32Array(n)
  const models = new Map<string, number>()
  const cats = new Map<string, number>()
  const categories: string[] = []
  for (let i = 0; i < n; i++) {
    const e = elements[i]
    boxes[i * 6] = e.box.min[0]
    boxes[i * 6 + 1] = e.box.min[1]
    boxes[i * 6 + 2] = e.box.min[2]
    boxes[i * 6 + 3] = e.box.max[0]
    boxes[i * 6 + 4] = e.box.max[1]
    boxes[i * 6 + 5] = e.box.max[2]
    let mi = models.get(e.modelId)
    if (mi === undefined) {
      mi = models.size
      models.set(e.modelId, mi)
    }
    modelIndex[i] = mi
    const cat = e.category ? e.category.toUpperCase() : ''
    if (!cat) {
      categoryIndex[i] = -1
      continue
    }
    let ci = cats.get(cat)
    if (ci === undefined) {
      ci = categories.length
      cats.set(cat, ci)
      categories.push(cat)
    }
    categoryIndex[i] = ci
  }
  return {
    boxes,
    modelIndex,
    categoryIndex,
    categories,
    config: normalizeClashConfig(config),
  }
}

/** Broad-phase sobre elementos "amigaveis". Atalho para testes e para
 *  quem nao precisa de empacotar a mao. */
export function broadPhase(
  elements: readonly ClashElementInput[],
  config?: Partial<ClashConfig> | null,
): BroadPhaseOutcome {
  return broadPhaseFlat(packBroadRequest(elements, config))
}

interface CategoryMasks {
  /** null = sem filtro. Indices de categoria admitidos. */
  allowed: Set<number> | null
  /** Matriz A x B activa. */
  matrix: boolean
  setA: Set<number>
  setB: Set<number>
  /** Elementos sem categoria entram no lado A / B? Nunca, quando ha
   *  matriz (nao ha como os classificar). */
  allowUncategorized: boolean
}

function buildMasks(req: BroadRequest): CategoryMasks {
  const f = req.config.categoryFilter
  const idx = new Map<string, number>()
  req.categories.forEach((c, i) => idx.set(c, i))
  const toSet = (names?: string[]): Set<number> => {
    const s = new Set<number>()
    for (const n of names ?? []) {
      const i = idx.get(String(n).toUpperCase())
      if (i !== undefined) s.add(i)
    }
    return s
  }
  if (!f) {
    return {
      allowed: null,
      matrix: false,
      setA: new Set(),
      setB: new Set(),
      allowUncategorized: true,
    }
  }
  let allowed: Set<number> | null = null
  if (f.include && f.include.length > 0) allowed = toSet(f.include)
  if (f.exclude && f.exclude.length > 0) {
    const ex = toSet(f.exclude)
    if (!allowed) {
      allowed = new Set<number>()
      for (let i = 0; i < req.categories.length; i++) {
        if (!ex.has(i)) allowed.add(i)
      }
    } else {
      for (const i of ex) allowed.delete(i)
    }
  }
  const matrix = !!(
    f.setA &&
    f.setA.length > 0 &&
    f.setB &&
    f.setB.length > 0
  )
  return {
    allowed,
    matrix,
    setA: matrix ? toSet(f.setA) : new Set(),
    setB: matrix ? toSet(f.setB) : new Set(),
    // Sem categoria nao passa por um `include` explicito nem por uma
    // matriz; passa quando so ha `exclude`.
    allowUncategorized: !(f.include && f.include.length > 0) && !matrix,
  }
}

function median(values: number[]): number {
  if (values.length === 0) return 0
  const s = values.slice().sort((a, b) => a - b)
  const mid = s.length >> 1
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

/** Afastamento entre duas caixas (0 quando se tocam ou sobrepoem). */
function boxGap(
  b: Float64Array,
  i: number,
  j: number,
): number {
  const ax0 = b[i * 6]
  const ay0 = b[i * 6 + 1]
  const az0 = b[i * 6 + 2]
  const ax1 = b[i * 6 + 3]
  const ay1 = b[i * 6 + 4]
  const az1 = b[i * 6 + 5]
  const bx0 = b[j * 6]
  const by0 = b[j * 6 + 1]
  const bz0 = b[j * 6 + 2]
  const bx1 = b[j * 6 + 3]
  const by1 = b[j * 6 + 4]
  const bz1 = b[j * 6 + 5]
  const dx = ax0 > bx1 ? ax0 - bx1 : bx0 > ax1 ? bx0 - ax1 : 0
  const dy = ay0 > by1 ? ay0 - by1 : by0 > ay1 ? by0 - ay1 : 0
  const dz = az0 > bz1 ? az0 - bz1 : bz0 > az1 ? bz0 - az1 : 0
  if (dx === 0 && dy === 0 && dz === 0) return 0
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

function hashCell(i: number, j: number, k: number): number {
  // Hash de Teschner et al. Os primos evitam que celulas vizinhas caiam
  // sistematicamente no mesmo balde.
  return ((i * 73856093) ^ (j * 19349663) ^ (k * 83492791)) >>> 0
}

/** Broad-phase sobre o pedido plano. Pura: mesma entrada, mesma saida. */
export function broadPhaseFlat(req: BroadRequest): BroadPhaseOutcome {
  const t0 = now()
  const cfg = req.config
  const boxes = req.boxes
  const n = req.modelIndex.length
  const masks = buildMasks(req)

  // 1. Quais elementos entram (filtro de categoria + caixa utilizavel).
  const active: number[] = []
  let filtered = 0
  for (let i = 0; i < n; i++) {
    const ci = req.categoryIndex[i]
    let ok = true
    if (masks.allowed) {
      ok = ci >= 0 ? masks.allowed.has(ci) : masks.allowUncategorized
    }
    if (ok && masks.matrix) {
      ok = ci >= 0 && (masks.setA.has(ci) || masks.setB.has(ci))
    }
    if (!ok) {
      filtered++
      continue
    }
    active.push(i)
  }

  const gapLimit = cfg.clearance > 0 ? cfg.clearance : 0
  const pairsOut: number[] = []
  let tested = 0
  let truncated = false
  let cellSize = 0
  let cells = 0

  if (active.length >= 2) {
    // 2. Tamanho da celula pela mediana da maior aresta das caixas.
    const extents: number[] = []
    for (const i of active) {
      const dx = boxes[i * 6 + 3] - boxes[i * 6]
      const dy = boxes[i * 6 + 4] - boxes[i * 6 + 1]
      const dz = boxes[i * 6 + 5] - boxes[i * 6 + 2]
      extents.push(Math.max(dx, dy, dz))
    }
    cellSize = Math.max(median(extents) + 2 * gapLimit, MIN_CELL)

    // 3. Inscricao na grelha, com duplicacao da celula se rebentar o
    //    orcamento de inscricoes.
    const grid = new Map<number, number[]>()
    const oversized: number[] = []
    for (let attempt = 0; ; attempt++) {
      grid.clear()
      oversized.length = 0
      let inserts = 0
      let blown = false
      for (const i of active) {
        const x0 = Math.floor((boxes[i * 6] - gapLimit) / cellSize)
        const y0 = Math.floor((boxes[i * 6 + 1] - gapLimit) / cellSize)
        const z0 = Math.floor((boxes[i * 6 + 2] - gapLimit) / cellSize)
        const x1 = Math.floor((boxes[i * 6 + 3] + gapLimit) / cellSize)
        const y1 = Math.floor((boxes[i * 6 + 4] + gapLimit) / cellSize)
        const z1 = Math.floor((boxes[i * 6 + 5] + gapLimit) / cellSize)
        const span = (x1 - x0 + 1) * (y1 - y0 + 1) * (z1 - z0 + 1)
        if (!Number.isFinite(span) || span > CELL_SPAN_CAP) {
          oversized.push(i)
          continue
        }
        inserts += span
        if (inserts > INSERT_BUDGET * active.length && attempt < MAX_REGROW) {
          blown = true
          break
        }
        for (let x = x0; x <= x1; x++) {
          for (let y = y0; y <= y1; y++) {
            for (let z = z0; z <= z1; z++) {
              const key = hashCell(x, y, z)
              const bucket = grid.get(key)
              if (bucket) bucket.push(i)
              else grid.set(key, [i])
            }
          }
        }
      }
      if (!blown) break
      cellSize *= 2
    }
    cells = grid.size

    // 4. Pares dentro de cada celula, com deduplicacao.
    //    A chave a*n+b cabe num double ate n ~ 94 milhoes.
    const seen = new Set<number>()
    const accept = (a: number, b: number): boolean => {
      if (!cfg.selfClash && req.modelIndex[a] === req.modelIndex[b]) return false
      if (masks.matrix) {
        const ca = req.categoryIndex[a]
        const cb = req.categoryIndex[b]
        const ab = masks.setA.has(ca) && masks.setB.has(cb)
        const ba = masks.setB.has(ca) && masks.setA.has(cb)
        if (!ab && !ba) return false
      }
      tested++
      return boxGap(boxes, a, b) <= gapLimit
    }
    const push = (i: number, j: number): boolean => {
      const a = i < j ? i : j
      const b = i < j ? j : i
      const key = a * n + b
      if (seen.has(key)) return true
      seen.add(key)
      if (!accept(a, b)) return true
      pairsOut.push(a, b)
      if (pairsOut.length >= cfg.maxPairs * 2) {
        truncated = true
        return false
      }
      return true
    }

    outer: for (const bucket of grid.values()) {
      const len = bucket.length
      if (len < 2) continue
      for (let p = 0; p < len - 1; p++) {
        for (let q = p + 1; q < len; q++) {
          if (!push(bucket[p], bucket[q])) break outer
        }
      }
    }
    if (!truncated && oversized.length > 0) {
      outer2: for (const big of oversized) {
        for (const other of active) {
          if (other === big) continue
          if (!push(big, other)) break outer2
        }
      }
    }
  }

  // 5. Ordem estavel por (a, b).
  const count = pairsOut.length / 2
  const order = new Uint32Array(count)
  for (let i = 0; i < count; i++) order[i] = i
  const sorted = Array.from(order).sort((i, j) => {
    const d = pairsOut[i * 2] - pairsOut[j * 2]
    return d !== 0 ? d : pairsOut[i * 2 + 1] - pairsOut[j * 2 + 1]
  })
  const pairs = new Uint32Array(count * 2)
  for (let i = 0; i < count; i++) {
    pairs[i * 2] = pairsOut[sorted[i] * 2]
    pairs[i * 2 + 1] = pairsOut[sorted[i] * 2 + 1]
  }

  return {
    pairs,
    tested,
    truncated,
    cellSize,
    cells,
    filtered,
    ms: now() - t0,
  }
}

/** `performance.now()` quando existe (browser, worker, node moderno),
 *  senao `Date.now()`. */
export function now(): number {
  return typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now()
}
